import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { type LocalizedContent, getLocalizedContent } from '#app/utils/i18n.ts'
import { cn } from '#app/utils/misc.tsx'

type DecorativeImagePositionStyles = Partial<
  Pick<
    CSSStyleDeclaration,
    | 'left'
    | 'top'
    | 'width'
    | 'minWidth'
    | 'height'
    | 'minHeight'
    | 'marginLeft'
    | 'marginTop'
    | 'opacity'
    | 'zIndex'
  >
>

type ImageContainerStyles = Pick<
  CSSStyleDeclaration,
  | 'width'
  | 'minWidth'
  | 'height'
  | 'minHeight'
  | 'marginLeft'
  | 'marginTop'
  | 'backgroundSize'
  | 'backgroundRepeat'
  | 'backgroundPosition'
  | 'maskClip'
>

interface Image {
  url: string | undefined
  width: number
  height: number
  marginLeft?: number
  marginTop?: number
  caption?: LocalizedContent
}

export const DecoratedImage = ({
  image,
  decoration,
  decorationOffset,
}: {
  image: Image
  decoration: Image
  decorationOffset: { left: number; top: number }
}) => {
  const imageRef = useRef<HTMLDivElement>(null)
  const [imageDecorationStyles, setImageDecorationStyles] =
    useState<DecorativeImagePositionStyles>({ opacity: '0' })
  const { i18n } = useTranslation()
  const locale = i18n.language

  useEffect(() => {
    if (imageRef.current?.style) {
      setImageDecorationStyles({
        marginLeft: `${decorationOffset.left}px`,
        marginTop: `${decorationOffset.top}px`,
        width: `${decoration.width}px`,
        minWidth: `${decoration.width}px`,
        height: `${decoration.height}px`,
        minHeight: `${decoration.height}px`,
        opacity: '1',
        zIndex: '1',
      })
    }
  }, [imageRef, decoration, decorationOffset])

  const imageContainerStyles: ImageContainerStyles = {
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    maskClip: 'border-box',
    width: `${image.width}px`,
    minWidth: `${image.width}px`,
    height: `${image.height}px`,
    minHeight: `${image.height}px`,
    marginLeft: image.marginLeft ? `${image.marginLeft}px` : '',
    marginTop: image.marginTop ? `${image.marginTop}px` : '',
  }

  const getImageContainerStyles = (
    backgroundImageUrl: string | undefined,
  ):
    | (ImageContainerStyles & {
        backgroundImage: CSSStyleDeclaration['backgroundImage']
      })
    | undefined => {
    if (!backgroundImageUrl) return undefined

    return {
      ...imageContainerStyles,
      backgroundImage: `url('${backgroundImageUrl}')`,
    }
  }

  const decorationClassName = cn(
    `absolute z-0`,
    `h-[${decoration.height}px] w-[${decoration.width}px] min-h-[${decoration.height}px] min-w-[${decoration.width}px]`,
  )

  return (
    <>
      <img
        role="presentation"
        className={decorationClassName}
        style={imageDecorationStyles}
        src={decoration.url}
      />
      <div
        ref={imageRef}
        className="rounded-6 relative z-10"
        style={getImageContainerStyles(image.url)}
      />
      {image.caption && (
        <p className="font-open-sans text-1 relative z-10 mt-4">
          {getLocalizedContent(image.caption, locale)}
        </p>
      )}
    </>
  )
}
